import { z } from "zod";

/**
 * Expression transforms — the function library for socket expressions.
 *
 * A transform is a pure, synchronous function invoked *inside* an expression
 * (`(concatenate (output "s" "a") (output "s" "b"))`). It is NOT a graph node:
 * no StepConfig, no category, no palette entry, no StepRun. It is the typed,
 * tested counterpart to the grammar's structural primitives (`output`,
 * `concat`) — the difference is that transforms are added to a registry rather
 * than hardcoded into the parser.
 *
 * ⚠️ Every transform MUST be synchronous and pure. Expressions are evaluated
 * inside the Temporal workflow VM, which re-executes them on every replay —
 * every deploy replays every open run. So: no I/O, no `Date.now()`/
 * `new Date()`/`Math.random()`, no `await`, same input ⇒ same output forever.
 * A transform that needs any of that is a step, not a transform.
 *
 * Positional, single-valued: expression calls pass arguments left-to-right and
 * yield one value on the consuming socket — so a transform takes a tuple of
 * args and returns a single value (unlike a node with named output sockets).
 *
 * The batch-mapping and validation machinery is ported from the lambda-nodes
 * work (PR #5416); the node framing is deliberately dropped.
 */
export interface TransformDefinition<
  TArgs extends z.ZodTuple = z.ZodTuple,
  TOut extends z.ZodType = z.ZodType,
> {
  /** The function name used in expressions, e.g. `"concatenate"`. */
  readonly name: string;
  /** Positional argument schemas; the call binds args left-to-right. */
  readonly args: TArgs;
  /** The single value the transform yields. */
  readonly output: TOut;
  readonly evaluate: (args: z.infer<TArgs>) => z.infer<TOut>;
}

/** Helper that preserves the arg/output types while erasing them for the registry. */
export function defineTransform<
  TArgs extends z.ZodTuple,
  TOut extends z.ZodType,
>(definition: TransformDefinition<TArgs, TOut>): TransformDefinition {
  return definition as unknown as TransformDefinition;
}

/** Which phase rejected: the transform's args or its output. */
export type TransformValidationPhase = "arguments" | "output";

export class TransformValidationError extends Error {
  constructor(
    // Not `name`: that is Error.name, overwritten below with the error type.
    readonly transformName: string,
    readonly phase: TransformValidationPhase,
    readonly detail: string,
    /** Index of the failing batch item, when the transform was mapped over one. */
    readonly itemIndex?: number,
  ) {
    super(
      `Transform "${transformName}" received invalid ${phase}${
        itemIndex === undefined ? "" : ` at batch item ${itemIndex}`
      }: ${detail}`,
    );
    this.name = "TransformValidationError";
  }
}

/**
 * string_join — appends the second string to the first (no separator). Distinct
 * from the grammar's `concat`, which flattens arrays for fan-in; this joins two
 * strings on the wire.
 */
export const stringJoinTransform = defineTransform({
  name: "string_join",
  args: z.tuple([z.string(), z.string()]),
  output: z.string(),
  evaluate: ([first, second]) => first + second,
});

export const TRANSFORM_DEFINITIONS: readonly TransformDefinition[] = [
  stringJoinTransform,
];

const TRANSFORMS_BY_NAME = new Map<string, TransformDefinition>(
  TRANSFORM_DEFINITIONS.map((transform) => [transform.name, transform]),
);

/** Whether a call name resolves to a registered transform. */
export function isTransform(name: string): boolean {
  return TRANSFORMS_BY_NAME.has(name);
}

export function getTransform(name: string): TransformDefinition {
  const transform = TRANSFORMS_BY_NAME.get(name);
  if (!transform) {
    throw new Error(`Transform "${name}" is not registered`);
  }
  return transform;
}

/** How many positional arguments a registered transform expects. */
export function getTransformArity(name: string): number {
  return getTransform(name).args.def.items.length;
}

/**
 * The socket-type string of a transform's output, for static type-compatibility
 * checks — `"string"` / `"number"` / `"boolean"` for scalar outputs, `undefined`
 * for anything richer (so validation stays permissive rather than falsely
 * rejecting). Runtime validation in {@link evaluateTransform} is the backstop.
 */
export function getTransformOutputType(name: string): string | undefined {
  const type = getTransform(name).output.def.type;
  return type === "string" || type === "number" || type === "boolean"
    ? type
    : undefined;
}

/**
 * Validate → evaluate → validate, mapping over a batch when one is wired in.
 * Pure and synchronous, so it is safe inside the Temporal workflow sandbox.
 *
 * **Batches.** Some producers emit a batch as parallel arrays on sockets
 * declared as single values, and the engine only splits those arrays at the
 * consuming step's fanout — so an expression evaluated before that split
 * receives the arrays whole. Rather than reject them, a transform **maps over
 * the batch**: arrays are zipped item for item, single values are broadcast to
 * every item, and the result comes back as an array of the same length, still
 * aligned with the producer's arrays so downstream fanout lines up.
 *
 * The decision is by fit, not declaration: args that satisfy the tuple whole
 * are evaluated whole; only args that do not fit are tried item-wise. Every
 * array present must be the same length — a transform cannot invent the
 * correspondence between mismatched batches.
 */
export function evaluateTransform(name: string, args: unknown[]): unknown {
  const transform = getTransform(name);

  const whole = transform.args.safeParse(args);
  if (whole.success) {
    return evaluateOnce(transform, whole.data);
  }

  const batchLength = resolveBatchLength(name, args);
  if (batchLength === null) {
    throw new TransformValidationError(
      name,
      "arguments",
      formatIssues(whole.error),
    );
  }

  const results: unknown[] = [];
  for (let index = 0; index < batchLength; index++) {
    const item = transform.args.safeParse(itemAt(args, index));
    if (!item.success) {
      throw new TransformValidationError(
        name,
        "arguments",
        formatIssues(item.error),
        index,
      );
    }
    results.push(evaluateOnce(transform, item.data));
  }
  return results;
}

function evaluateOnce(
  transform: TransformDefinition,
  args: z.infer<z.ZodTuple>,
): unknown {
  const output = transform.output.safeParse(transform.evaluate(args));
  if (!output.success) {
    throw new TransformValidationError(
      transform.name,
      "output",
      formatIssues(output.error),
    );
  }
  return output.data;
}

/**
 * How many items the wired-in batch holds, or `null` when no argument is an
 * array and there is therefore nothing to map over.
 */
function resolveBatchLength(name: string, args: unknown[]): number | null {
  const lengths = args
    .filter(Array.isArray)
    .map((value: unknown[]) => value.length);

  if (lengths.length === 0) return null;

  const distinct = [...new Set(lengths)];
  if (distinct.length > 1) {
    throw new TransformValidationError(
      name,
      "arguments",
      `batched arguments hold different numbers of items (${distinct
        .sort((a, b) => a - b)
        .join(
          ", ",
        )}); arrays passed to one transform must line up item for item`,
    );
  }
  return distinct[0];
}

/** The batch narrowed to one item: arrays indexed, single values broadcast. */
function itemAt(args: unknown[], index: number): unknown[] {
  return args.map((value) => (Array.isArray(value) ? value[index] : value));
}

/** Zod issues flattened to a single readable line. */
function formatIssues(error: z.ZodError): string {
  return error.issues
    .map((issue) => {
      const at = issue.path.length > 0 ? `arg ${String(issue.path[0])}: ` : "";
      return `${at}${issue.message}`;
    })
    .join("; ");
}
